import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { ServerContextService } from '../../../../feature-vault/src/lib/servers/server-context.service';
import { vaultTenantHeaders } from '@nebula/shared-vault/data-access';
import {
  FilePreservationStatus,
  PreservationFileListResponse,
  PreservationRepairResponse,
  PreservationStatistics,
  PreservationStatus,
} from '../../../../domain/src/lib/preservation/preservation.model';

@Injectable({ providedIn: 'root' })
export class PreservationService {
  private readonly http = inject(HttpClient);
  private readonly serverContext = inject(ServerContextService);
  private readonly preservationPath = '/api/preservation';

  private preservationUrl(): string {
    return this.preservationPath;
  }

  private tenantHeaders(): HttpHeaders {
    return vaultTenantHeaders(this.serverContext.getContext().tenantId);
  }

  /**
   * Algunos despliegues devuelven `{ files, total }`, otros `{ items|results, total_files }`
   * e incluso listas planas. Normalizamos para no dejar tablas vacias por shape distinto.
   */
  private normalizeFileListResponse(raw: unknown): PreservationFileListResponse {
    if (Array.isArray(raw)) {
      return {
        files: raw as FilePreservationStatus[],
        total: raw.length,
      };
    }
    if (raw && typeof raw === 'object') {
      const record = raw as Record<string, unknown>;
      const filesCandidate =
        record['files'] ??
        record['items'] ??
        record['results'] ??
        record['data'];
      const files = Array.isArray(filesCandidate)
        ? (filesCandidate as FilePreservationStatus[])
        : [];

      const totalCandidate =
        record['total'] ??
        record['total_files'] ??
        record['count'] ??
        record['len'];
      const total = Number(totalCandidate);
      return {
        files,
        total: Number.isFinite(total) ? total : files.length,
      };
    }
    return { files: [], total: 0 };
  }

  getStatus(): Observable<PreservationStatus> {
    return this.http.get<PreservationStatus>(`${this.preservationUrl()}/status`, {
      headers: this.tenantHeaders(),
    });
  }

  getFileStatus(filePath: string): Observable<FilePreservationStatus> {
    const params = new HttpParams().set('file_path', filePath);
    return this.http.get<FilePreservationStatus>(`${this.preservationUrl()}/file`, {
      headers: this.tenantHeaders(),
      params,
    });
  }

  markAsOptimal(filePath: string): Observable<unknown> {
    return this.http.put(
      `${this.preservationUrl()}/file/mark-optimal`,
      { file_path: filePath },
      { headers: this.tenantHeaders() },
    );
  }

  diagnoseFile(filePath: string): Observable<unknown> {
    const params = new HttpParams().set('file_path', filePath);
    return this.http.get(`${this.preservationUrl()}/file/diagnose`, {
      headers: this.tenantHeaders(),
      params,
    });
  }

  repairFile(filePath: string, issues: string[]): Observable<PreservationRepairResponse> {
    return this.http.post<PreservationRepairResponse>(
      `${this.preservationUrl()}/file/repair`,
      { file_path: filePath, issues },
      { headers: this.tenantHeaders() },
    );
  }

  getCriticalFiles(limit = 50): Observable<PreservationFileListResponse> {
    const params = new HttpParams().set('limit', limit);
    return this.http
      .get<unknown>(`${this.preservationUrl()}/critical`, {
        headers: this.tenantHeaders(),
        params,
      })
      .pipe(map((raw) => this.normalizeFileListResponse(raw)));
  }

  getAttentionFiles(limit = 50): Observable<PreservationFileListResponse> {
    const params = new HttpParams().set('limit', limit);
    return this.http
      .get<unknown>(`${this.preservationUrl()}/attention`, {
        headers: this.tenantHeaders(),
        params,
      })
      .pipe(map((raw) => this.normalizeFileListResponse(raw)));
  }

  getOptimalFiles(limit = 50): Observable<PreservationFileListResponse> {
    const params = new HttpParams().set('limit', limit);
    return this.http
      .get<unknown>(`${this.preservationUrl()}/optimal`, {
        headers: this.tenantHeaders(),
        params,
      })
      .pipe(map((raw) => this.normalizeFileListResponse(raw)));
  }

  getStatistics(): Observable<PreservationStatistics> {
    return this.http.get<PreservationStatistics>(
      `${this.preservationUrl()}/reports/statistics`,
      { headers: this.tenantHeaders() },
    );
  }

  verifyFiles(filePaths: string[]): Observable<unknown> {
    return this.http.post(
      `${this.preservationUrl()}/verify-files`,
      { file_paths: filePaths },
      { headers: this.tenantHeaders() },
    );
  }
}
